import { getMasteryTagsProgressData } from "@helpers/progress-data";

/* -----------------Child components--------------- */
import theme from "@configs/theme";
import {
	mockedMasteryCardData,
	mockedSummaryCardData,
	mockedTagProgressData,
} from "@constants/mocked-data";

import type {
	AppConfigMasteryLevelsType,
	AppConfigStatsType,
	CardSchemaType,
} from "@configs/custom-app-config-schema";
import { isScreenshotMode } from "@constants/common";
import { isI18nKeyExists } from "@helpers/common";
import {
	getMasteryLabels,
	getProgressColorScale,
} from "@helpers/content/distribution";
import i18n from "@helpers/lib/i18n";
import { useAppStore } from "@hooks/useAppStore";
import type { AppTheme } from "@hooks/useAppTheme";
import {
	formatSecondsToUTCtime,
	smartPluralize,
} from "@memorang/helpers/src/common";
import type { BaseItemSchema } from "@memorang/types/src/session/new-session";
import type { ReportDetails } from "@memorang/types/src/session/report";
import type { IconType } from "@memorang/ui/src/types";
import type { TFunction } from "i18next";
import {
	type Skill,
	type SkillDetails,
	getCompositeScoreAndLevel,
} from "./point-normalization";

const statsValueMap = (
	reportDetails: ReportDetails,
): Record<string, string> => ({
	correct: `${reportDetails.numCorrect} / ${reportDetails.numQuestions}`,
	accuracy: `${Math.round(reportDetails.accuracy)}%`,
	duration: formatSecondsToUTCtime(reportDetails.timeElapsed),
	avgSpeed: `${Number(reportDetails.avgSpeed).toFixed(1)} secs`,
	hintsUsed: `${reportDetails.numHints}`,
	pointsEarned: `${reportDetails.numPointsEarned} / ${reportDetails.numTotalPoints}`,
	answered: `${reportDetails.answered}`,
	timeElapsed: `${reportDetails.timeElapsed.toFixed(0)}`,
});

export const getSessionReportInfoText = ({
	numActivities,
	numPointsEarned,
	numTotalPoints,
	skills,
	compositeLevel,
	compositeFeedback,
	t,
}: {
	numActivities: number;
	numPointsEarned: number;
	numTotalPoints: number;
	skills: SkillDetails[];
	compositeLevel: string;
	compositeFeedback: string;
	t: TFunction;
}) => {
	const skillNames = skills.map((s) =>
		t(`dashboard.skills.${s.skill}`).toLowerCase(),
	);

	if (skills.length === 1) {
		const skill = skills[0];
		return t("report.pointsInfo.infoText.singleSkill", {
			itemCount: numActivities,
			itemLabel: smartPluralize("item", numActivities),
			skill_1: t(`dashboard.skills.${skill.skill}`).toLowerCase(),
			skill_1_score: skill.numPointsEarned,
			skill_1_total: skill.numTotalPoints,
			proficiency: t(`session.masteryLevels.${compositeLevel}`).toLowerCase(),
			compositeFeedback,
		});
	}

	const skillsText = `${skillNames.slice(0, -1).join(", ")} ${t("common.and")} ${skillNames[skillNames.length - 1]}`;

	const skillDetails = skills
		.map((skill) =>
			t("report.pointsInfo.skillDetails", {
				skill: t(`dashboard.skills.${skill.skill}`).toLowerCase(),
				score: skill.numPointsEarned,
				total: skill.numTotalPoints,
				proficiency: t(`session.masteryLevels.${skill.level}`).toLowerCase(),
			}),
		)
		.join(" ");

	return t("report.pointsInfo.infoText.multiSkill", {
		itemCount: numActivities,
		itemLabel: smartPluralize("item", numActivities),
		skills_text: skillsText,
		skill_details: skillDetails,
		overall_score: numPointsEarned,
		overall_total: numTotalPoints,
		overall_proficiency: t(
			`session.masteryLevels.${compositeLevel}`,
		).toLowerCase(),
		compositeFeedback,
	});
};

const getSummaryCardData = (
	response: ReportDetails,
	t: TFunction,
	sessionSummaryStats: AppConfigStatsType,
	maxScore: number,
	studyPackCollectionId?: number,
) => {
	const isSummativeTest = response.reportSessionType === "SUMMATIVE";

	const getTitle = () => {
		switch (response.reportSessionType) {
			case "DIAGNOSTIC":
				return t("report.diagnosticSessionCompleted");
			case "SUMMATIVE":
				return studyPackCollectionId === 38
					? t("report.diagnosticSessionCompleted")
					: t("report.practiceTestCompleted");
			case "PRACTICETEST":
				return t("report.practiceTestCompleted");
			case "STUDYPACK":
				return t("report.studySessionCompleted");
			default:
				return t("report.studySessionCompleted");
		}
	};

	const title = getTitle();

	const filterStats = sessionSummaryStats.filter((stat) => {
		if (stat.type === "hintsUsed" && !response.numHints && isSummativeTest) {
			return false;
		}
		if (stat.type === "pointsEarned" && !response.numTotalPoints) {
			return false;
		}

		if (stat.type === "accuracy" && isSummativeTest) {
			return false;
		}
		return true;
	});

	const newStats = filterStats.map((stat) => ({
		...stat,
		value: statsValueMap(response)[stat.type],
		overlineText: isI18nKeyExists(`report.stats.${stat.title.toLowerCase()}`)
			? t(`report.stats.${stat.title.toLowerCase()}`)
			: stat.title,
	}));

	const summaryCardData = {
		title,
		score: Number.parseInt(response.score),
		numPointsEarned: response.numPointsEarned || 0,
		numTotalPoints: response.numTotalPoints || 0,
		numItems: response.numQuestions,
		maxScore,
		stats: newStats,
	};

	return summaryCardData;
};
const getPerformanceCardData = (
	response: ReportDetails,
	t: TFunction,
	showPointsBasedScore: boolean,
) => {
	if (!showPointsBasedScore) {
		return {
			title: "Performance",
			subtitle: `Based on ${response.numQuestions} items`,
			sections: [
				{
					sectionTitle: "",
					items: [
						{
							title: "Correct",
							rightText: `${response.numCorrect} items`,
							icon: "thumb-up-outline" as IconType,
							color: theme().colors.success.main,
						},
						{
							title: "Incorrect",
							rightText: `${response.numIncorrect} items`,
							icon: "thumb-down-outline" as IconType,
							color: theme().colors.error,
						},
					],
				},
			],
		};
	}
	const performanceCardData = {
		title: t("report.performance.title"),
		subtitle: t("report.performance.subtitle", {
			count: response.numQuestions,
		}),
		sections: [
			{
				sectionTitle: "",
				items: [
					{
						title: t("common.correct"),
						rightText: t("report.performance.correctItems", {
							count: response.numCorrect,
						}),
						icon: "thumb-up-outline" as IconType,
						color: theme().colors.success.main,
					},
					{
						title: t("common.incorrect"),
						rightText: t("report.performance.incorrectItems", {
							count: response.numIncorrect,
						}),
						icon: "thumb-down-outline" as IconType,
						color: theme().colors.error,
					},
				],
			},
		],
	};
	return performanceCardData;
};

const getMasteryCardData = ({
	distribution,
	numMarked,
	numSkipped,
	isSummativeTest,
	numAnswered,
	t,
	sessionMasteryCardData,
	showPointsBasedScore,
	masteryLevels,
}: {
	numQuestions: number;
	distribution: ReportDetails["distribution"];
	numMarked?: number;
	numSkipped?: number;
	isSummativeTest?: boolean;
	numAnswered?: number;
	t: TFunction;
	sessionMasteryCardData?: CardSchemaType;
	showPointsBasedScore: boolean;
	masteryLevels: AppConfigMasteryLevelsType;
}) => {
	if (!distribution) {
		return null;
	}

	const title = isSummativeTest
		? t("report.mastery.performanceTitle")
		: sessionMasteryCardData?.title?.[i18n.language] ||
			t("report.mastery.sessionMasteryTitle");

	const practiceTestMasteryCardData = {
		title,
		subtitle: t("report.mastery.subtitle", {
			count: numAnswered || 0,
			item: smartPluralize("item", numAnswered || 0),
			sessionType: "assessment",
		}),
		sections: [
			{
				items: [
					{
						title: t("common.correct"),
						count: distribution.high,
						icon: "thumb-up-outline" as IconType,
						type: "correct" as const,
						color: theme().colors.progress.high,
					},
					{
						title: t("common.incorrect"),
						type: "incorrect" as const,
						count: distribution.low,
						icon: "thumb-down-outline" as IconType,
						color: theme().colors.progress.low,
					},
					...(numMarked != null
						? [
								{
									icon: "flag-outline" as IconType,
									title: t("common.marked"),
									count: numMarked,
									type: "marked" as const,
									color: theme().colors.secondary,
								},
							]
						: []),
					...(numSkipped != null
						? [
								{
									title: t("common.skipped"),
									count: numSkipped,
									type: "skipped" as const,
									icon: "minus-circle-outline" as IconType,
									color: theme().colors.progress.noneBackground,
								},
							]
						: []),
				],
			},
		],
	};

	const masterLabels = getMasteryLabels();

	if (!showPointsBasedScore) {
		return isSummativeTest
			? practiceTestMasteryCardData
			: {
					title,
					subtitle: t("report.mastery.subtitle", {
						count: numAnswered,
						item: smartPluralize("item", numAnswered || 0),
						sessionType: "session",
					}),
					sections: [
						{
							sectionTitle: "Correct",
							items: [
								{
									title: masterLabels.high,
									count: distribution.high,
									icon: "speedometer" as IconType,
									type: "correct" as const,
									color: theme().colors.progress.high,
								},
								{
									title: masterLabels.medium,
									count: distribution.medium,
									type: "almostcorrect" as const,
									icon: "speedometer-medium" as IconType,
									color: theme().colors.progress.medium,
								},
							],
						},
						{
							sectionTitle: "Incorrect",
							items: [
								{
									title: masterLabels.low,
									type: "incorrect" as const,
									count: distribution.low,
									icon: "speedometer-slow" as IconType,
									color: theme().colors.progress.low,
								},
							],
						},
					],
				};
	}
	const masteryCardData = {
		title,
		subtitle: t("report.mastery.subtitle", {
			count: numAnswered,
			item: smartPluralize("item", numAnswered || 0),
			sessionType: isSummativeTest ? "assessment" : "session",
		}),
		sections: [
			{
				items: masteryLevels.map((item) => ({
					title: t(item.alias.title),
					count: distribution[item.id],
					icon: item.icon as IconType,
					color: item.lightColor,
					type: item.id as "correct" | "incorrect" | "skipped" | "marked",
				})),
			},
		],
	};

	return masteryCardData;
};
const getReviewListItemData = ({
	distribution,
	isSummativeTest,
	numSkipped,
	numMarked,
	numAnswered,
	t,
	showPointsBasedScore,
	numQuestions,
	sessionMasteryCardData,
	masteryLevels,
}: {
	distribution: ReportDetails["distribution"];
	numAnswered?: number;
	numSkipped?: number;
	numMarked?: number;
	isSummativeTest?: boolean;
	t: TFunction;
	sessionMasteryCardData?: CardSchemaType;
	showPointsBasedScore: boolean;
	numQuestions: number;
	masteryLevels: AppConfigMasteryLevelsType;
}) => {
	const reviewListItemsDataForQuestion = getMasteryCardData({
		distribution,
		isSummativeTest,
		numMarked,
		numSkipped,
		t,
		numAnswered,
		sessionMasteryCardData,
		showPointsBasedScore,
		numQuestions,
		masteryLevels,
	});

	return {
		QUESTION: {
			items: reviewListItemsDataForQuestion,
			numIncorrect: distribution.low,
		},
	};
};

export const getItemsPerformanceCardData = (items: BaseItemSchema[]) => {
	return {
		title: "Item review",
		subtitle: `Based on all ${items.length} items from this assessment`,
		items: items.map((item, index) => ({
			id: item.id!,
			title: item.content?.stem!,
			tags: item.tags!,
			marked: item.answer?.marked,
			index,
			answerStatus: item.answer?.selectedChoiceIds?.length
				? item.answer?.correct
					? ("correct" as const)
					: ("incorrect" as const)
				: ("skipped" as const),
		})),
	};
};

const getReportData = (
	response: ReportDetails,
	t: TFunction,
	theme: AppTheme,
	isSummativeTest?: boolean,
	studyPackCollectionId?: number,
) => {
	const {
		markedCount: numMarked,
		numSkipped,
		numIncorrect,
		numCorrect,
		examName,
		numQuestions,
	} = response;

	const sessionSummaryStats = useAppStore.getState().reportView?.stats!;
	const showPointsBasedScore =
		useAppStore.getState().reportView?.formative?.showPointsBasedScore!;
	const masteryLevels = useAppStore
		.getState()
		.app.masteryLevels!.sort((a, b) => a.sortOrder - b.sortOrder);
	const scoreRanges = useAppStore.getState().app.scoreRanges!;
	const maxScore = useAppStore.getState().reportView?.formative?.maxScore!;
	const summaryCardData = isScreenshotMode
		? mockedSummaryCardData
		: getSummaryCardData(
				response,
				t,
				sessionSummaryStats,
				maxScore,
				studyPackCollectionId,
			);

	const performanceCardData = getPerformanceCardData(
		response,
		t,
		showPointsBasedScore,
	);

	const sessionMasteryCardData = useAppStore
		.getState()
		.reportView?.cards?.find((item) => item.type === "session-mastery");

	const tagMasteryCardDataItem = useAppStore
		.getState()
		.reportView?.cards?.find((item) => item.type === "tag-mastery");

	const masteryCardData = isScreenshotMode
		? mockedMasteryCardData
		: getMasteryCardData({
				distribution: response.distribution,
				numMarked,
				numSkipped,
				isSummativeTest,
				t,
				numAnswered: response.answered,
				numQuestions,
				showPointsBasedScore,
				masteryLevels,
				sessionMasteryCardData,
			});
	const reviewListItemsData = response.distribution
		? getReviewListItemData({
				distribution: response.distribution,
				isSummativeTest,
				numSkipped,
				numMarked,
				t,
				numAnswered: response.answered,
				numQuestions,
				showPointsBasedScore,
				masteryLevels,
				sessionMasteryCardData,
			})
		: null;
	const masteryTagProgressData = isScreenshotMode
		? mockedTagProgressData
		: getMasteryTagsProgressData({
				tags: response.tagDetails,
				currentView: "Progress",
				numItems: response.answered,
				t,
				theme,
				tagMasteryCardDataItem,
			});

	const skills = response.tagDetails.map((tag) => ({
		skill: tag.label as Skill,
		numPointsEarned: tag.numPointsEarned || 0,
		numTotalPoints: tag.numTotalPoints || 0,
	}));

	const compositeScoreAndLevel = showPointsBasedScore
		? getCompositeScoreAndLevel(skills, t)
		: undefined;
	const sessionReportInfoText = compositeScoreAndLevel
		? getSessionReportInfoText({
				numActivities: response.numQuestions,
				numPointsEarned: response.numPointsEarned || 0,
				numTotalPoints: response.numTotalPoints || 0,
				skills: compositeScoreAndLevel.skillDetails,
				compositeLevel: compositeScoreAndLevel?.compositeLevel,
				compositeFeedback: compositeScoreAndLevel.compositeFeedback,
				t,
			})
		: undefined;

	return {
		summaryCardData: {
			...summaryCardData,
			colorScale: showPointsBasedScore
				? [compositeScoreAndLevel?.compositeColor, theme.colors.progress.none]
				: getProgressColorScale(
						Number(response.score || 0),
						theme,
						scoreRanges,
					),
			compositeScore: showPointsBasedScore
				? compositeScoreAndLevel?.compositeScaledScore
				: undefined,
			sessionReportInfoText,
		},
		performanceCardData,
		masteryTagProgressData,
		masteryCardData,
		reviewListItemsData,
		numCorrect,
		numIncorrect,
		sessionId: response.id,
		sessionTitle: examName,
	};
};

export { getReportData };
